/**
 * EventsCalendar — month/week grid of events, reached from the events list.
 *
 * Each day cell shows one chip per event TYPE with a count, not a list of
 * titles: the point of this view is to get to a date+type-filtered list, which
 * the events list already supports via ?type=&from=&to=. Clicking a chip carries
 * the calendar's own position (calDate/calView) into the URL so "Back to
 * calendar" returns to the exact month or week the operator left, not a reset
 * view.
 *
 * Three decisions worth knowing, all confirmed with the owner:
 *
 *  1. MULTI-DAY events (gun shows run several days) render on EVERY day they
 *     run, and the day-filtered list means "occurring that day", not "starting
 *     that day". A gun show therefore appears in several cells, which is what a
 *     calendar implies.
 *  2. DATE-TBD events cannot be placed on a grid at all. They get their own tray
 *     so they do not silently vanish from a view people will start treating as
 *     the complete picture.
 *  3. Each event is placed on its EVENT-LOCAL date (events carry their own
 *     `timezone`), never the viewer's. A 7pm Pacific event must not slide onto
 *     the next day for a Central viewer — this codebase has already been bitten
 *     by that class of bug once.
 *
 * Cancelled/postponed events are shown rather than hidden: an operator looking
 * at a date wants to know something WAS scheduled there. A chip whose events are
 * all cancelled renders muted.
 *
 * This deliberately does not reuse SalesCalendarView. That component is the rep
 * availability/assignment calendar — it colours by rep, carries availability
 * modals, and has a narrower role gate. Its react-big-calendar month view also
 * renders one block per event, which is the opposite of the per-type aggregate
 * this view needs. The event-type colours ARE reused.
 */
import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import Button from "../components/common/Button";
import { getEventScheduleFields, getEvents } from "../services/events.service";
import { EVENT_STATUSES } from "../utils/constants";
import { US_STATE_OPTIONS } from "../types/list.types";
import {
  EVENT_TYPE_LABELS,
  getEventTypeColor,
} from "../types/salesCalendar.types";
import {
  isEventLiveNow,
  DERIVABLE_EVENT_STATUSES,
} from "../utils/eventLiveStatus";
import { useNowTick } from "../hooks/useNowTick";

type ViewMode = "month" | "week";

const ISO_DATE_RX = /^\d{4}-\d{2}-\d{2}$/;

// These MUST mirror the list page (pages/Events.tsx). The calendar links into that list,
// so if the two disagreed about what "active" means the operator would click a chip
// showing N events and land on a list showing a different N.
const HIDDEN_STATUSES = ["cancelled"];
const SAFE_STATUS: ReadonlySet<string> = new Set([
  "active",
  "all",
  ...EVENT_STATUSES.map((s) => s.value),
]);
const SAFE_SETUP: ReadonlySet<string> = new Set([
  "all",
  "incomplete",
  "complete",
]);

/** Same translation the list uses, so "what you see is what you get" holds across both. */
function statusQueryParams(value: string): {
  status?: string;
  excludeStatuses?: string[];
} {
  if (value === "active") {
    return { excludeStatuses: HIDDEN_STATUSES };
  }
  if (value === "all") {
    return {};
  }
  return { status: value };
}
const WEEK_OPTS = { weekStartsOn: 1 as const }; // Monday, matching SalesCalendar

/** Statuses that mean "this is not happening" — chips render muted. */
const INACTIVE_STATUSES = new Set(["cancelled", "canceled"]);

interface CalendarEventRow {
  id: string;
  title?: string | null;
  type?: string | null;
  status?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  timezone?: string | null;
  all_day?: boolean | null;
  date_tbd?: boolean | null;
}

/**
 * The event's own local calendar date, as YYYY-MM-DD.
 *
 * `start_time` is a timestamptz stored in UTC. Formatting it in the browser's
 * zone would place a late-evening Pacific event on the following day for a
 * Central viewer. Intl with the event's `timezone` gives the date the event
 * actually happens on. Falls back to the plain `start_date` column (already a
 * wall-clock date) and finally to the viewer's zone if neither is usable.
 */
function eventLocalDate(
  iso: string | null | undefined,
  timezone: string | null | undefined,
  fallbackDate?: string | null,
): string | null {
  if (!iso) {
    return fallbackDate && ISO_DATE_RX.test(fallbackDate) ? fallbackDate : null;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return fallbackDate && ISO_DATE_RX.test(fallbackDate) ? fallbackDate : null;
  }
  try {
    // en-CA renders as YYYY-MM-DD, which is exactly the key format we want.
    return new Intl.DateTimeFormat("en-CA", {
      // GS-04: the same zone the list behind a chip uses (get_events_for_organizations: COALESCE(timezone, 'UTC')),
      // never the viewer's, so a chip never leads to an empty list.
      timeZone: timezone || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    // An unknown/invalid IANA zone must not blank the calendar.
    return format(d, "yyyy-MM-dd");
  }
}

/** Every local date an event occupies, inclusive, capped so bad data cannot hang the render. */
function occupiedDates(ev: CalendarEventRow): string[] {
  const start = eventLocalDate(ev.start_time, ev.timezone, ev.start_date);
  if (!start) {
    return [];
  }
  const end = eventLocalDate(ev.end_time, ev.timezone, ev.end_date) ?? start;
  if (end <= start) {
    return [start];
  }
  const out: string[] = [];
  let cursor = parseISO(start);
  const last = parseISO(end);
  // 60 days is far beyond any real event and stops a corrupt end_date from
  // spinning here.
  for (let i = 0; i <= 60 && cursor <= last; i += 1) {
    out.push(format(cursor, "yyyy-MM-dd"));
    cursor = addDays(cursor, 1);
  }
  return out;
}

const EventsCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = searchParams.get("calView");
  const [view, setView] = useState<ViewMode>(
    viewParam === "week" ? "week" : "month",
  );

  // Filters live in the URL under the SAME keys the list uses, so a chip click can hand
  // them straight over and "Back to calendar" can hand them straight back.
  const readSafe = (
    key: string,
    fallback: string,
    allowed?: ReadonlySet<string>,
  ) => {
    const v = searchParams.get(key);
    if (v === null || (allowed && !allowed.has(v))) {
      return fallback;
    }
    return v;
  };
  // Default "active" mirrors the list: cancelled events are hidden until asked for.
  const statusFilter = readSafe("status", "active", SAFE_STATUS);
  const setupFilter = readSafe("setup", "all", SAFE_SETUP);
  const stateFilter = readSafe("state", "all");
  const setupIncompleteParam =
    setupFilter === "incomplete"
      ? true
      : setupFilter === "complete"
        ? false
        : undefined;

  const setFilter = (key: string, value: string, droppableDefault: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === droppableDefault) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const anchorParam = searchParams.get("calDate");
  const [anchor, setAnchor] = useState<Date>(() => {
    if (anchorParam && ISO_DATE_RX.test(anchorParam)) {
      const d = parseISO(anchorParam);
      if (!Number.isNaN(d.getTime())) {
        return d;
      }
    }
    return new Date();
  });

  // Keep the calendar's position in the URL so a chip click can carry it into
  // the list and the list can hand it straight back.
  const setPosition = (nextAnchor: Date, nextView: ViewMode) => {
    setAnchor(nextAnchor);
    setView(nextView);
    const next = new URLSearchParams(searchParams);
    next.set("calDate", format(nextAnchor, "yyyy-MM-dd"));
    next.set("calView", nextView);
    setSearchParams(next, { replace: true });
  };

  const { gridStart, gridEnd, periodLabel } = useMemo(() => {
    if (view === "week") {
      const s = startOfWeek(anchor, WEEK_OPTS);
      const e = endOfWeek(anchor, WEEK_OPTS);
      return {
        gridStart: s,
        gridEnd: e,
        periodLabel: `${format(s, "d MMM")} – ${format(e, "d MMM yyyy")}`,
      };
    }
    const s = startOfWeek(startOfMonth(anchor), WEEK_OPTS);
    const e = endOfWeek(endOfMonth(anchor), WEEK_OPTS);
    return {
      gridStart: s,
      gridEnd: e,
      periodLabel: format(anchor, "MMMM yyyy"),
    };
  }, [anchor, view]);

  const fromKey = format(gridStart, "yyyy-MM-dd");
  const toKey = format(gridEnd, "yyyy-MM-dd");

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "events-calendar",
      fromKey,
      toKey,
      statusFilter,
      setupFilter,
      stateFilter,
    ],
    queryFn: async () => {
      const res = await getEvents({
        // Widen the lower bound: a multi-day event can START before the grid and
        // still RUN inside it. The query filters on start_time, so without this
        // a gun show beginning the previous week would be missing from its own
        // remaining days. Client-side filtering trims the surplus.
        dateFrom: format(addDays(gridStart, -14), "yyyy-MM-dd"),
        dateTo: toKey,
        // Cancelled events are hidden by default, exactly as on the list. They are still
        // reachable by switching the Status filter, and when shown they render muted —
        // an operator looking at a date should be able to see something WAS scheduled.
        ...statusQueryParams(statusFilter),
        setupIncomplete: setupIncompleteParam,
        state: stateFilter === "all" ? undefined : stateFilter,
        page: 1,
        limit: 500,
        sortField: "start_time",
        sortDirection: "asc",
      });
      // GS-04: the date-filtered query returns no timezone/date_tbd, so every event was placed on the viewer's day
      // while the list behind a chip matches days in the event's zone (a gun show showed on a day whose list was
      // empty). Fill the schedule columns in; if that read fails, eventLocalDate falls back to the list's own zone.
      const rows = (res?.events ?? []) as CalendarEventRow[];
      try {
        const extra = await getEventScheduleFields(
          rows.map((r) => r.id).filter((x): x is string => !!x),
        );
        return {
          ...res,
          events: rows.map((r) => {
            const x = r.id ? extra.get(r.id) : undefined;
            return x ? { ...r, ...x, id: r.id } : r;
          }),
        };
      } catch (e) {
        console.warn("EventsCalendar: schedule columns not read", e);
        return res;
      }
    },
    staleTime: 60_000,
  });

  const events = useMemo<CalendarEventRow[]>(
    () => (data?.events ?? []) as CalendarEventRow[],
    [data],
  );

  // Re-evaluated every tick so a chip starts (and stops) marking itself live
  // while the calendar sits open, instead of freezing at mount time.
  const nowTick = useNowTick();

  /** date -> type -> { total, inactive, live } */
  const byDate = useMemo(() => {
    const map = new Map<
      string,
      Map<string, { total: number; inactive: number; live: number }>
    >();
    for (const ev of events) {
      if (ev.date_tbd) {
        continue; // handled by the TBD tray
      }
      const type = ev.type || "other";
      const isInactive = INACTIVE_STATUSES.has((ev.status || "").toLowerCase());
      // "Happening right now", derived from the event's own timestamps by the
      // same shared helper the status badge uses — the stored status is not
      // evidence of liveness (nothing automates scheduled -> in_progress).
      // A cancelled/completed/postponed event is never live, whatever its
      // window says, matching deriveEventDisplayStatus's precedence.
      const isLive =
        !isInactive &&
        DERIVABLE_EVENT_STATUSES.has(
          (ev.status || "scheduled").toLowerCase(),
        ) &&
        isEventLiveNow(ev, nowTick);
      for (const day of occupiedDates(ev)) {
        if (day < fromKey || day > toKey) {
          continue;
        }
        let types = map.get(day);
        if (!types) {
          types = new Map();
          map.set(day, types);
        }
        const cur = types.get(type) ?? { total: 0, inactive: 0, live: 0 };
        cur.total += 1;
        if (isInactive) {
          cur.inactive += 1;
        }
        if (isLive) {
          cur.live += 1;
        }
        types.set(type, cur);
      }
    }
    return map;
  }, [events, fromKey, toKey, nowTick]);

  const tbdEvents = useMemo(() => events.filter((e) => e.date_tbd), [events]);

  const days = useMemo(() => {
    const out: Date[] = [];
    let cursor = gridStart;
    while (cursor <= gridEnd) {
      out.push(cursor);
      cursor = addDays(cursor, 1);
    }
    return out;
  }, [gridStart, gridEnd]);

  /** Deep link into the existing list, carrying the calendar position back. */
  const listHref = (day: string, type: string) => {
    const p = new URLSearchParams();
    p.set("type", type);
    p.set("from", day);
    p.set("to", day);
    // Hand the calendar's own filters over verbatim. The count on the chip was produced
    // under these filters, so the list must be filtered identically or the operator
    // clicks "3 Gun Show" and lands on a list of 5.
    p.set("status", statusFilter);
    if (setupFilter !== "all") {
      p.set("setup", setupFilter);
    }
    if (stateFilter !== "all") {
      p.set("state", stateFilter);
    }
    p.set("ret", "cal");
    p.set("calDate", format(anchor, "yyyy-MM-dd"));
    p.set("calView", view);
    return `/events?${p.toString()}`;
  };

  const step = (dir: -1 | 1) =>
    setPosition(
      view === "week" ? addWeeks(anchor, dir) : addMonths(anchor, dir),
      view,
    );

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <CalendarDaysIcon className="h-6 w-6 text-gray-500" />
          <h1 className="text-2xl font-semibold text-gray-900">
            Events Calendar
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            // ?view=list is load-bearing: a bare /events now redirects here, so the link
            // needs a parameter or the two views ping-pong.
            to="/events?view=list"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ListBulletIcon className="h-4 w-4" /> List view
          </Link>
          <Button onClick={() => navigate("/events/create")}>
            Create Event
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label={view === "week" ? "Previous week" : "Previous month"}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setPosition(new Date(), view)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label={view === "week" ? "Next week" : "Next month"}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          <span className="ml-2 text-lg font-medium text-gray-900">
            {periodLabel}
          </span>
        </div>

        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          {(["month", "week"] as ViewMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setPosition(anchor, m)}
              className={`px-3 py-2 text-sm capitalize ${
                view === m
                  ? "bg-primary-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Same filters, same keys and same defaults as the list view. Type is deliberately
          NOT offered here: type is the dimension the day cells already break out, and a
          type filter would leave the operator staring at cells that silently omit events. */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          Status:
          <select
            value={statusFilter}
            onChange={(e) => setFilter("status", e.target.value, "active")}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="active">Active (hide cancelled)</option>
            <option value="all">All statuses</option>
            {EVENT_STATUSES.map((st) => (
              <option key={st.value} value={st.value}>
                {st.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          Setup:
          <select
            value={setupFilter}
            onChange={(e) => setFilter("setup", e.target.value, "all")}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All setup</option>
            <option value="incomplete">Incomplete only</option>
            <option value="complete">Complete only</option>
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          State:
          <select
            value={stateFilter}
            onChange={(e) => setFilter("state", e.target.value, "all")}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All States</option>
            {US_STATE_OPTIONS.map((st) => (
              <option key={st.code} value={st.code}>
                {st.name}
              </option>
            ))}
          </select>
        </label>

        {(statusFilter !== "active" ||
          setupFilter !== "all" ||
          stateFilter !== "all") && (
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("status");
              next.delete("setup");
              next.delete("state");
              setSearchParams(next, { replace: true });
            }}
            className="text-sm text-primary-600 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-800"
        >
          Could not load events for this period.
        </div>
      )}

      <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div
            key={d}
            className="bg-gray-50 px-2 py-2 text-xs font-medium text-gray-500 text-center"
          >
            {d}
          </div>
        ))}

        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const types = byDate.get(key);
          const outside = view === "month" && !isSameMonth(day, anchor);
          return (
            <div
              key={key}
              className={`bg-white min-h-[104px] p-2 align-top ${
                outside ? "opacity-50" : ""
              }`}
            >
              <div
                className={`text-xs mb-1 ${
                  isToday(day)
                    ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white font-semibold"
                    : "text-gray-500"
                }`}
              >
                {format(day, "d")}
              </div>
              <div className="flex flex-col gap-1">
                {types &&
                  [...types.entries()]
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([type, counts]) => {
                      const allInactive =
                        counts.inactive > 0 && counts.inactive === counts.total;
                      const label =
                        EVENT_TYPE_LABELS[type] ??
                        type
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase());
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => navigate(listHref(key, type))}
                          title={[
                            `${label}: ${counts.total}`,
                            counts.inactive > 0
                              ? `${counts.inactive} cancelled`
                              : null,
                            counts.live > 0
                              ? `${counts.live} happening now`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          className={`w-full text-left text-[11px] leading-4 px-1.5 py-1 rounded border truncate hover:ring-2 hover:ring-primary-300 ${
                            allInactive
                              ? "line-through text-gray-400 border-gray-200 bg-gray-50"
                              : "text-gray-800 border-transparent"
                          } ${counts.live > 0 ? "ring-2 ring-yellow-400 font-semibold" : ""}`}
                          style={
                            allInactive
                              ? undefined
                              : {
                                  backgroundColor: `${getEventTypeColor(type)}22`,
                                  borderColor: getEventTypeColor(type),
                                }
                          }
                        >
                          {counts.live > 0 && (
                            <span
                              aria-hidden="true"
                              className="inline-block h-1.5 w-1.5 rounded-full bg-yellow-500 mr-1 align-middle"
                            />
                          )}
                          {counts.total} {label}
                          {counts.live > 0 && (
                            <span className="sr-only">
                              , {counts.live} happening now
                            </span>
                          )}
                        </button>
                      );
                    })}
              </div>
            </div>
          );
        })}
      </div>

      {isLoading && (
        <div className="mt-4 text-sm text-gray-500">Loading events…</div>
      )}

      {tbdEvents.length > 0 && (
        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h2 className="text-sm font-semibold text-amber-900 mb-2">
            Date to be confirmed ({tbdEvents.length})
          </h2>
          <p className="text-xs text-amber-800 mb-3">
            These events have no confirmed date, so they cannot appear on the
            grid above. They are listed here so the calendar is not mistaken for
            the complete picture.
          </p>
          <ul className="flex flex-wrap gap-2">
            {tbdEvents.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/events/${e.id}`}
                  className="inline-block text-xs px-2 py-1 rounded border border-amber-300 bg-white hover:bg-amber-100"
                >
                  {e.title || "(untitled)"}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default EventsCalendar;
